/**
 * `Empty` — one pattern, different string, forever. `docs/design-direction.md` §8.7.
 *
 * **Never an illustration, never a mascot, never a shrug emoji, never a centred
 * headline-plus-paragraph** (§12.8). Every empty state in this app is the real grid,
 * one object drawn from the system's own vocabulary, one mono line, and at most one
 * 44px action. There is not a single `<img>`, `<svg>` illustration or emoji in this
 * file, and the test asserts all three by reading the rendered tree.
 *
 * The four configured variants of §8.7:
 *
 * | variant | object | string | action |
 * |---|---|---|---|
 * | `crate` | a 200px dubplate, label completely blank cream, one chinagraph word | `Ничего не нарезано` | — |
 * | `search` | one crate divider card, flipped forward | `Не найдено в ящике` | clear the query |
 * | `queue` | the cream set-list sheet, hand-numbered 1–8, no titles | `Пустой сет-лист` | put a selection on |
 * | `favorites` | a 4×4 grid of empty label-stock slots, 1px dashed | `Ящик пуст` | — |
 *
 * ## Two deliberate deviations from the letter of §8.7
 *
 * 1. **The strings are sentence case, not caps.** §8.7 writes them uppercase, but
 *    §4.3 and §12.16 are explicit that uppercase is licensed for Latin and numerals
 *    only — tracked Cyrillic caps read as shouting, and the audience is Russian. The
 *    mono tier survives; the shouting does not. The literals live in
 *    {@link EMPTY_COPY} so a screen asserts against the constant rather than
 *    re-typing a string this file owns.
 * 2. **The empty-search variant offers one action, not three suggestion stamps.** The
 *    pattern is one object, one string, **≤1 action**; three stamps is four controls
 *    in a state that is supposed to be quiet. The suggestion stamps of §8.3 belong to
 *    the search screen, which renders them beside this frame rather than inside it.
 *
 * An empty screen is an invitation to act, so the string says what is not there in
 * the interface's own voice and the action — when there is one — is the next thing a
 * selector would actually do.
 */

import type { JSX } from 'react';

import { Paper } from '../../components/primitives/Paper';
import { cx } from '../../components/primitives/Rule';

import type { StateGrid } from './Loading';
import { StateFrame } from './Loading';
import styles from './states.module.css';

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

export type EmptyVariant = 'crate' | 'search' | 'queue' | 'favorites';

export interface EmptyCopy {
  /** The one mono line. Russian, sentence case (see the header). */
  readonly string: string;
  /** The single 44px action's label, or `null` when the state offers none. */
  readonly action: string | null;
  /**
   * One 15px line of UI prose, only where the state has something to teach — the
   * `[ ]` filing affordance of §8.5. Never a second message.
   */
  readonly note: string | null;
  /** The grid this state occupies when the caller does not name one. */
  readonly grid: StateGrid;
}

/** The whole copy surface of the empty layer, in one readable table. */
export const EMPTY_COPY: Readonly<Record<EmptyVariant, EmptyCopy>> = Object.freeze({
  crate: Object.freeze({
    string: 'Ничего не нарезано',
    action: null,
    note: null,
    grid: 'ledger',
  }),
  search: Object.freeze({
    string: 'Не найдено в ящике',
    action: 'Очистить запрос',
    note: null,
    grid: 'ledger',
  }),
  queue: Object.freeze({
    string: 'Пустой сет-лист',
    action: 'Поставить подборку',
    note: null,
    grid: 'sheet',
  }),
  favorites: Object.freeze({
    string: 'Ящик пуст',
    action: null,
    note: 'Нажмите [ ] на записи — она отправится в ящик.',
    grid: 'shelf',
  }),
});

/**
 * The word a plant writes on a virgin acetate, in chinagraph, at −3.5°.
 *
 * It names the object — a blank label — and it is not the state message: the state
 * message is the mono Russian line. That distinction is what keeps this inside the
 * two licensed uses of Caveat (§4.1, §12.15) instead of making handwriting carry UI
 * copy, which the contract bans outright.
 */
const SCRAWL = 'BLANK';

/** The hand numbering on an empty set-list sheet: 1–8, and no titles (§8.7). */
const SHEET_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

/* ══ the objects ═══════════════════════════════════════════════════════════════════ */

/**
 * One object per variant, all four drawn in CSS from the system's own vocabulary: a
 * record, a sleeve card, a sheet of label stock. The only figurative graphics in the
 * entire app are objects from the *production* of the music (§11).
 */
function EmptyObject({ variant }: { readonly variant: EmptyVariant }): JSX.Element {
  switch (variant) {
    case 'crate':
      return (
        <div className={styles.object} data-object="crate">
          {/* `.disc` carries `animation-play-state: paused` with no `data-playing`,
              so the blank plate is motionless — the needle at rest (§7.2, §7.4). */}
          <div className={cx('disc', styles.blank)} aria-hidden="true">
            <div className={cx('disc__label', styles.blankLabel)}>
              <span className={cx('scrawl', styles.scrawl)}>{SCRAWL}</span>
            </div>
            <div className="disc__spindle" />
          </div>
        </div>
      );

    case 'search':
      return (
        <div className={styles.object} data-object="search">
          <div className={styles.dividerCard} aria-hidden="true" />
        </div>
      );

    case 'queue':
      return (
        <div className={styles.object} data-object="queue">
          {/* The one large light surface the contract licenses, and the only place
              other than the disc label where paper appears at all (§6.11, §8.4). */}
          <Paper className={styles.sheetStub} tape={2}>
            <ol className={styles.numbers} aria-hidden="true">
              {SHEET_NUMBERS.map((number) => (
                <li key={number} className="scrawl">
                  {number}
                </li>
              ))}
            </ol>
          </Paper>
        </div>
      );

    case 'favorites':
      return (
        <div className={styles.object} data-object="favorites">
          <div className={styles.stock} aria-hidden="true">
            {Array.from({ length: 16 }, (_unused, index) => (
              <span key={index} />
            ))}
          </div>
        </div>
      );
  }
}

/* ══ Empty ═════════════════════════════════════════════════════════════════════════ */

export interface EmptyProps {
  readonly variant: EmptyVariant;
  /** Overrides the variant's default grid when a screen puts it somewhere else. */
  readonly grid?: StateGrid;
  /**
   * Runs the variant's single action. **Omit it and no button is rendered** — an
   * action that goes nowhere is worse than no action, and the pattern allows at most
   * one either way.
   */
  readonly onAction?: (() => void) | undefined;
  readonly className?: string | undefined;
}

export function Empty({ variant, grid, onAction, className }: EmptyProps): JSX.Element {
  const copy = EMPTY_COPY[variant];
  const showAction = copy.action !== null && onAction !== undefined;

  return (
    <StateFrame
      grid={grid ?? copy.grid}
      state="empty"
      className={className}
      role="group"
      ariaLabel={copy.string}
    >
      <EmptyObject variant={variant} />

      <p className={cx('t-mono', 't-13', styles.cyr, styles.string)} data-state-string={variant}>
        {copy.string}
      </p>

      {copy.note === null ? null : <p className={cx('t-ui', 't-15', styles.note)}>{copy.note}</p>}

      {showAction ? (
        <button
          type="button"
          className={cx('t-ui', 't-17', styles.action)}
          onClick={onAction}
          data-action="empty"
        >
          {copy.action}
        </button>
      ) : null}
    </StateFrame>
  );
}
